import { Popover, ActionIcon, SimpleGrid, Tooltip, ScrollArea, TextInput, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { IconMoodSmile } from '@tabler/icons-react';

const EMOJI_LIST: { emoji: string; label: string }[] = [
  // Tech
  { emoji: '💻', label: 'Laptop' },
  { emoji: '🖥️', label: 'Desktop' },
  { emoji: '📱', label: 'Phone' },
  { emoji: '⌨️', label: 'Keyboard' },
  { emoji: '🖱️', label: 'Mouse' },
  { emoji: '🎮', label: 'Gaming' },
  { emoji: '📺', label: 'TV' },
  { emoji: '🔌', label: 'Electronics' },
  { emoji: '💡', label: 'Smart Home' },
  { emoji: '🫧', label: 'Appliances' },
  { emoji: '📷', label: 'Camera' },
  { emoji: '🎧', label: 'Headphones' },
  { emoji: '🖨️', label: 'Printer' },
  { emoji: '🔋', label: 'Battery' },
  { emoji: '📡', label: 'Satellite' },
  // Entertainment
  { emoji: '🎬', label: 'Movies' },
  { emoji: '📚', label: 'Books' },
  { emoji: '🎵', label: 'Music' },
  { emoji: '🧸', label: 'Toys' },
  { emoji: '🎯', label: 'Games' },
  { emoji: '🎲', label: 'Board Games' },
  { emoji: '🃏', label: 'Cards' },
  { emoji: '🎪', label: 'Fun' },
  // Fashion
  { emoji: '👗', label: 'Dress' },
  { emoji: '👕', label: 'T-Shirt' },
  { emoji: '👟', label: 'Shoes' },
  { emoji: '👜', label: 'Bag' },
  { emoji: '💍', label: 'Jewelry' },
  { emoji: '🧣', label: 'Scarf' },
  { emoji: '🧤', label: 'Gloves' },
  { emoji: '🧥', label: 'Coat' },
  { emoji: '👒', label: 'Hat' },
  { emoji: '👓', label: 'Glasses' },
  { emoji: '⌚', label: 'Watch' },
  // Food & Drink
  { emoji: '🍽️', label: 'Dining' },
  { emoji: '☕', label: 'Coffee' },
  { emoji: '🍷', label: 'Wine' },
  { emoji: '🥤', label: 'Drink' },
  { emoji: '🍞', label: 'Bread' },
  { emoji: '🥣', label: 'Cereal' },
  { emoji: '🥫', label: 'Canned' },
  { emoji: '🍳', label: 'Cooking' },
  { emoji: '🥛', label: 'Dairy' },
  { emoji: '🧊', label: 'Frozen' },
  { emoji: '🌍', label: 'International' },
  { emoji: '🥩', label: 'Meat' },
  { emoji: '🥦', label: 'Produce' },
  { emoji: '🍿', label: 'Snacks' },
  { emoji: '🫙', label: 'Condiments' },
  { emoji: '🧂', label: 'Salt' },
  { emoji: '🫖', label: 'Tea' },
  { emoji: '🍺', label: 'Beer' },
  { emoji: '🧃', label: 'Juice' },
  // Health & Beauty
  { emoji: '💅', label: 'Beauty' },
  { emoji: '🌸', label: 'Fragrance' },
  { emoji: '💇', label: 'Hair' },
  { emoji: '💄', label: 'Makeup' },
  { emoji: '🪥', label: 'Personal Care' },
  { emoji: '🧴', label: 'Skincare' },
  { emoji: '💊', label: 'Vitamins' },
  { emoji: '🧼', label: 'Soap' },
  { emoji: '🪞', label: 'Mirror' },
  { emoji: '🩺', label: 'Medical' },
  { emoji: '🏃', label: 'Fitness' },
  // Home
  { emoji: '🏡', label: 'House' },
  { emoji: '🏠', label: 'Home' },
  { emoji: '🛁', label: 'Bath' },
  { emoji: '🧹', label: 'Cleaning' },
  { emoji: '🛋️', label: 'Furniture' },
  { emoji: '🌿', label: 'Garden' },
  { emoji: '🔧', label: 'Tools' },
  { emoji: '🪑', label: 'Chair' },
  { emoji: '🛏️', label: 'Bed' },
  { emoji: '🪴', label: 'Plant' },
  { emoji: '🕯️', label: 'Candle' },
  { emoji: '🧺', label: 'Laundry' },
  { emoji: '🔑', label: 'Key' },
  // Everything Else
  { emoji: '🗂️', label: 'Other' },
  { emoji: '📦', label: 'Package' },
  { emoji: '🚗', label: 'Car' },
  { emoji: '🍼', label: 'Baby' },
  { emoji: '📎', label: 'Office' },
  { emoji: '🐾', label: 'Pets' },
  { emoji: '🏋️', label: 'Sports' },
  { emoji: '✈️', label: 'Travel' },
  { emoji: '⛺', label: 'Outdoor' },
  { emoji: '🎒', label: 'Backpack' },
  { emoji: '🐶', label: 'Dog' },
  { emoji: '🐱', label: 'Cat' },
  { emoji: '🚲', label: 'Bike' },
  { emoji: '🛹', label: 'Sports Gear' },
];

interface EmojiPickerPopoverProps {
  value: string;
  onChange: (emoji: string) => void;
}

export function EmojiPickerPopover({ value, onChange }: EmojiPickerPopoverProps) {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = search.trim()
    ? EMOJI_LIST.filter((e) => e.label.toLowerCase().includes(search.toLowerCase()))
    : EMOJI_LIST;

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-start" shadow="md" width={280}>
      <Popover.Target>
        <Tooltip label="Pick an emoji">
          <ActionIcon
            variant="light"
            size="lg"
            onClick={() => setOpened((o) => !o)}
            style={{ marginTop: 24 }}
          >
            {value.trim() ? (
              <span style={{ fontSize: 18, lineHeight: 1 }}>{value.trim()}</span>
            ) : (
              <IconMoodSmile size={18} />
            )}
          </ActionIcon>
        </Tooltip>
      </Popover.Target>

      <Popover.Dropdown>
        <Stack gap="xs">
          <TextInput
            placeholder="Search emojis..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="xs"
            autoFocus
          />
          <ScrollArea h={220}>
            {filtered.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="sm">No results</Text>
            ) : (
              <SimpleGrid cols={7} spacing={4}>
                {filtered.map(({ emoji, label }) => (
                  <Tooltip key={emoji} label={label} position="top" withArrow openDelay={300}>
                    <ActionIcon
                      variant={value === emoji ? 'filled' : 'subtle'}
                      size="md"
                      onClick={() => {
                        onChange(emoji);
                        setOpened(false);
                        setSearch('');
                      }}
                      style={{ fontSize: 18 }}
                    >
                      {emoji}
                    </ActionIcon>
                  </Tooltip>
                ))}
              </SimpleGrid>
            )}
          </ScrollArea>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
